<template>
    <div class="players-tab">
        <!-- ---------- MVP ---------- -->
        <div
            v-if="mvp && mvpPlayer"
            class="mvp-card"
            :class="{ 'mvp-card--clickable': mvpPlayer.id }"
            :style="{ '--team-color': mvpPlayer.color }"
            @click="goToPlayer(mvpPlayer.id)"
        >
            <img v-if="mvpPlayer.teamLogo" :src="mvpPlayer.teamLogo" alt="" class="mvp-card__watermark">
            <div class="mvp-card__photo">
                <img :src="mvpPlayer.image" :alt="mvpPlayer.name" @error="$event.target.src = defaultPlayerImg">
            </div>
            <div class="mvp-card__info">
                <span class="mvp-card__badge">
                    <v-icon x-small color="white">mdi-crown</v-icon>
                    MVP του αγώνα
                </span>
                <span class="mvp-card__team">{{ mvpPlayer.teamName }}</span>
                <span class="mvp-card__name">{{ mvpPlayer.name }}</span>
                <span class="mvp-card__pir">{{ mvpPlayer.pir }} <small>PIR</small></span>
                <div class="mvp-card__stats">
                    <span v-for="item in mvpPlayer.line" :key="item.label" class="mvp-stat">
                        <b>{{ item.value }}</b>
                        <small>{{ item.label }}</small>
                    </span>
                </div>
            </div>
        </div>

        <!-- ---------- Leaders per category ---------- -->
        <div class="leaders">
            <div class="leaders__subtitle">Κορυφαίοι ανά κατηγορία</div>
            <div class="leaders__teams">
                <router-link v-for="side in teams" :key="side.key" :to="teamRoute(side.team)" class="leaders__team" :class="`leaders__team--${side.key}`">
                    <span class="leaders__logo">
                        <img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" alt="">
                        <v-icon v-else color="orange">mdi-basketball</v-icon>
                    </span>
                    <span class="leaders__team-name">{{ side.team.name }}</span>
                </router-link>
            </div>

            <div v-for="row in leaderRows" :key="row.key" class="leader-row">
                <div
                    v-for="side in row.sides"
                    :key="side.key"
                    class="leader-side"
                    :class="[`leader-side--${side.key}`, { 'leader-side--lead': row.lead === side.key, 'leader-side--clickable': side.player }]"
                    @click="side.player && goToPlayer(side.player.id)"
                >
                    <template v-if="side.player">
                        <img :src="side.player.image" alt="" class="leader-side__photo" :style="{ borderColor: side.color }" @error="$event.target.src = defaultPlayerImg">
                        <span class="leader-side__name">{{ side.player.name }}</span>
                        <span class="leader-side__value">
                            {{ side.display }}
                            <small v-if="side.detail">{{ side.detail }}</small>
                        </span>
                    </template>
                    <span v-else class="leader-side__empty">—</span>
                </div>
                <div class="leader-row__label">{{ row.label }}</div>
            </div>
            <div class="leaders__note">Στα ποσοστά μετράνε παίκτες με τουλάχιστον 3 σουτ (2 βολές).</div>
        </div>

        <!-- ---------- Box score ---------- -->
        <match-stats :match="match" />
    </div>
</template>

<script>
    import MatchStats from '@/components/MatchStats'
    import { getTeamColors } from '@/utils/shotChart'
    import { getStats, getPir, getPlayerImage, getShortName, getJersey, hasPlayerStats, percentage } from '@/utils/playerStats'

    const ASSETS_URL = 'https://storage.googleapis.com'

    // `min`: attempts needed to count for a percentage
    const CATEGORIES = [
        { key: 'points', label: 'Πόντοι' },
        { key: 'two', label: 'Δίποντα', made: 'twoPointsMade', attempts: 'twoPointAttempts', min: 3 },
        { key: 'three', label: 'Τρίποντα', made: 'threePointsMade', attempts: 'threePointAttempts', min: 3 },
        { key: 'free', label: 'Βολές', made: 'freeThrowsMade', attempts: 'freeThrowAttempts', min: 2 },
        { key: 'offensiveRebounds', label: 'Επιθ. ριμπάουντ' },
        { key: 'defensiveRebounds', label: 'Αμυντ. ριμπάουντ' },
        { key: 'rebounds', label: 'Ριμπάουντ' },
        { key: 'assists', label: 'Ασίστ' },
        { key: 'steals', label: 'Κλεψίματα' },
        { key: 'blockedShots', label: 'Κοψίματα' },
        { key: 'pir', label: 'PIR' }
    ]

    export default {
        components: { MatchStats },
        props: {
            match: {
                type: Object,
                default: null
            },
            mvp: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                assetsUrl: ASSETS_URL,
                defaultPlayerImg: require('@/assets/default_player.png')
            }
        },
        computed: {
            colors() {
                return getTeamColors(this.match && this.match.team1, this.match && this.match.team2)
            },
            teams() {
                return ['team1', 'team2'].map(key => ({
                    key,
                    team: (this.match && this.match[key]) || {},
                    color: this.colors[key]
                }))
            },
            // players who took part, with the values every category needs
            players() {
                const result = {}
                this.teams.forEach((side) => {
                    const players = Array.isArray(side.team.players) ? side.team.players : []
                    result[side.key] = players.filter(hasPlayerStats).map((player) => {
                        const stats = getStats(player)
                        const jersey = getJersey(player)
                        return {
                            id: player.id,
                            name: getShortName(player),
                            fullName: `${jersey !== null ? `#${jersey} ` : ''}${player.name || ''} ${player.surname || ''}`.trim(),
                            image: getPlayerImage(player, this.defaultPlayerImg),
                            stats,
                            pir: getPir(stats)
                        }
                    })
                })
                return result
            },
            leaderRows() {
                return CATEGORIES.map((category) => {
                    const sides = this.teams.map(side => ({ key: side.key, color: side.color, ...this.getLeader(this.players[side.key], category) }))
                    const [first, second] = sides
                    let lead = null
                    if (first.player && (!second.player || first.value > second.value)) lead = 'team1'
                    if (second.player && (!first.player || second.value > first.value)) lead = 'team2'
                    return { key: category.key, label: category.label, sides, lead }
                })
            },
            // best PIR of the winning team
            mvpPlayer() {
                const score1 = Number(this.match && this.match.team1Score) || 0
                const score2 = Number(this.match && this.match.team2Score) || 0
                if (score1 === score2) return null
                const key = score1 > score2 ? 'team1' : 'team2'
                const best = (this.players[key] || []).reduce((top, player) => (!top || player.pir > top.pir ? player : top), null)
                if (!best || best.pir <= 0) return null
                const team = this.match[key] || {}
                const stats = best.stats
                const fgMade = (Number(stats.twoPointsMade) || 0) + (Number(stats.threePointsMade) || 0)
                const fgAttempts = (Number(stats.twoPointAttempts) || 0) + (Number(stats.threePointAttempts) || 0)
                return {
                    ...best,
                    name: best.fullName,
                    color: this.colors[key],
                    teamName: team.name,
                    teamLogo: team.logo ? `${ASSETS_URL}/${team.logo}` : null,
                    line: [
                        { label: 'Πόντοι', value: Number(stats.points) || 0 },
                        { label: 'Ριμπάουντ', value: Number(stats.rebounds) || 0 },
                        { label: 'Ασίστ', value: Number(stats.assists) || 0 },
                        { label: 'Κλεψίματα', value: Number(stats.steals) || 0 },
                        { label: 'Κοψίματα', value: Number(stats.blockedShots) || 0 },
                        { label: 'Σουτ', value: `${fgMade}/${fgAttempts}` }
                    ]
                }
            }
        },
        methods: {
            getLeader(players = [], category) {
                let best = null
                players.forEach((player) => {
                    let value
                    let attempts = 0
                    if (category.made) {
                        attempts = Number(player.stats[category.attempts]) || 0
                        if (attempts < category.min) return
                        value = (Number(player.stats[category.made]) || 0) / attempts
                    } else {
                        value = category.key === 'pir' ? player.pir : Number(player.stats[category.key]) || 0
                    }
                    // ties go to the player with more attempts
                    if (!best || value > best.value || (value === best.value && attempts > best.attempts)) {
                        best = { player, value, attempts }
                    }
                })
                if (!best || (!category.made && best.value <= 0)) return { player: null, value: 0 }
                if (category.made) {
                    const made = Number(best.player.stats[category.made]) || 0
                    return { player: best.player, value: best.value, display: `${percentage(made, best.attempts)}%`, detail: `${made}/${best.attempts}` }
                }
                return { player: best.player, value: best.value, display: best.value, detail: null }
            },
            goToPlayer(id) {
                if (id) this.$router.push({ name: 'Player', params: { id } })
            },
            teamRoute(team) {
                return team && team.id ? { name: 'Team', params: { id: team.id } } : {}
            }
        }
    }
</script>

<style scoped>
.players-tab {
    display: flex;
    flex-direction: column;
    gap: 20px;
    font-family: Ubuntu, Arial, sans-serif;
}

/* ---------- MVP ---------- */
.mvp-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 18px 24px;
    border-left: 6px solid var(--team-color);
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.mvp-card--clickable {
    cursor: pointer;
}

.mvp-card--clickable:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.14);
}

.mvp-card__watermark {
    position: absolute;
    top: 50%;
    left: 110px;
    height: 190%;
    transform: translate(-50%, -50%);
    opacity: 0.14;
    pointer-events: none;
}

.mvp-card__photo {
    position: relative;
    flex: 0 0 auto;
    width: 180px;
    height: 200px;
}

.mvp-card__photo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: bottom;
}

.mvp-card__info {
    position: relative;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
}

.mvp-card__badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    border-radius: 999px;
    background: linear-gradient(90deg, #ef4444, #ff7133, orange);
    color: #fff;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.mvp-card__team {
    margin-top: 8px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.mvp-card__name {
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.9rem;
    font-weight: 800;
    line-height: 1.2;
    word-break: break-word;
}

.mvp-card__pir {
    background-image: linear-gradient(90deg, #ef4444 0%, #ff7133 70%, orange 100%);
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 2.6rem;
    font-weight: 800;
    line-height: 1.1;
}

.mvp-card__pir small {
    font-size: 1rem;
}

.mvp-card__stats {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
}

.mvp-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 72px;
    padding: 6px 10px;
    border-radius: 10px;
    background: #f4f4f6;
    line-height: 1.15;
}

.mvp-stat b {
    color: #111;
    font-size: 1.15rem;
    font-variant-numeric: tabular-nums;
}

.mvp-stat small {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.68rem;
    font-weight: 700;
}

/* ---------- Leaders ---------- */
.leaders {
    padding: 16px 18px 12px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.leaders__subtitle {
    margin-bottom: 12px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.leaders__teams {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.leaders__team {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    color: #2b2b2e !important;
    text-decoration: none;
}

.leaders__team--team2 {
    flex-direction: row-reverse;
    text-align: right;
}

.leaders__logo {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 3px;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14);
}

.leaders__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.leaders__team-name {
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.1rem;
    font-weight: 800;
    line-height: 1.15;
}

.leaders__team:hover .leaders__team-name {
    color: #ef4444;
}

.leader-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 130px minmax(0, 1fr);
    align-items: center;
    gap: 10px;
    padding: 6px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.leader-row__label {
    grid-column: 2;
    grid-row: 1;
    color: rgba(0, 0, 0, 0.65);
    font-size: 0.8rem;
    font-weight: 700;
    text-align: center;
    white-space: nowrap;
}

.leader-side {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 4px 6px;
    border-radius: 10px;
}

.leader-side--team1 {
    grid-column: 1;
    grid-row: 1;
}

.leader-side--team2 {
    flex-direction: row-reverse;
    grid-column: 3;
    grid-row: 1;
    text-align: right;
}

.leader-side--clickable {
    cursor: pointer;
}

.leader-side--clickable:hover {
    background: #f4f4f6;
}

.leader-side__photo {
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    border: 2px solid;
    border-radius: 50%;
    background: #f1f1f3;
    object-fit: cover;
    object-position: top;
}

.leader-side__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    color: #2b2b2e;
    font-size: 0.88rem;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.leader-side__value {
    display: flex;
    flex-direction: column;
    color: rgba(0, 0, 0, 0.6);
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
}

.leader-side--team1 .leader-side__value {
    align-items: flex-end;
}

.leader-side__value small {
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.7rem;
    font-weight: 600;
}

.leader-side--lead .leader-side__value {
    color: #111;
    font-size: 1.15rem;
    font-weight: 800;
}

.leader-side__empty {
    width: 100%;
    color: rgba(0, 0, 0, 0.3);
    text-align: center;
}

.leaders__note {
    padding-top: 8px;
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.72rem;
    text-align: center;
}

@media (max-width: 600px) {
    .mvp-card {
        gap: 12px;
        padding: 14px 12px;
    }

    .mvp-card__watermark {
        left: 60px;
        height: 150%;
    }

    .mvp-card__photo {
        width: 100px;
        height: 130px;
    }

    .mvp-card__name {
        font-size: 1.3rem;
    }

    .mvp-card__pir {
        font-size: 2rem;
    }

    .mvp-card__stats {
        gap: 5px;
    }

    .mvp-stat {
        min-width: 0;
        padding: 4px 7px;
    }

    .mvp-stat b {
        font-size: 0.95rem;
    }

    .mvp-stat small {
        font-size: 0.6rem;
    }

    .leaders {
        padding: 14px 10px 8px;
    }

    .leaders__team-name {
        font-size: 0.95rem;
    }

    /* the category on its own line, the two players under it */
    .leader-row {
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 4px 0;
        padding: 10px 0;
    }

    .leader-side--team1 {
        grid-column: 1;
        grid-row: 2;
    }

    .leader-row__label {
        grid-column: 1 / -1;
        color: rgba(0, 0, 0, 0.5);
        font-size: 0.7rem;
        font-weight: 800;
        letter-spacing: 0.05em;
        text-transform: uppercase;
    }

    /* photo on the outside, name over the value: nothing has to share one line */
    .leader-side {
        display: grid;
        grid-template-areas: 'photo name' 'photo value';
        grid-template-columns: 36px minmax(0, 1fr);
        column-gap: 8px;
        row-gap: 1px;
        align-items: center;
        padding: 2px 8px 2px 0;
    }

    .leader-side--team2 {
        grid-column: 2;
        grid-row: 2;
        grid-template-areas: 'name photo' 'value photo';
        grid-template-columns: minmax(0, 1fr) 36px;
        padding: 2px 0 2px 8px;
        border-left: 1px solid rgba(0, 0, 0, 0.07);
    }

    .leader-side__photo {
        grid-area: photo;
        width: 36px;
        height: 36px;
    }

    .leader-side__name {
        grid-area: name;
        font-size: 0.76rem;
        font-weight: 600;
        color: rgba(0, 0, 0, 0.65);
    }

    .leader-side__value,
    .leader-side--team1 .leader-side__value {
        grid-area: value;
        flex-direction: row;
        align-items: baseline;
        gap: 5px;
        font-size: 1rem;
    }

    .leader-side--team2 .leader-side__value {
        justify-content: flex-end;
    }

    .leader-side--lead .leader-side__value {
        font-size: 1.1rem;
    }

    .leader-side__empty {
        grid-column: 1 / -1;
    }
}
</style>
